import React, { useState, useCallback } from 'react'
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Space,
  Row,
  Col,
  Card,
  Typography,
  message,
  Tooltip,
  Switch,
  Divider,
  notification
} from 'antd'
import {
  faHeartPulse,
  faTemperatureHalf,
  faLungs,
  faDroplet,
  faStethoscope,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import dayjs from 'dayjs'
import { createClinicDataRecords } from '../../../examination-utils/examination-lib'
import { VITAL_SIGN_REFERENCE_RANGES } from '../utils/vital-signs-utils'
import { hexToRGBA, handleError } from '../../../../lib/utils'

const { Title, Text } = Typography

const VitalSignsCaptureModal = ({
  visible,
  onClose,
  bookingData,
  userRef,
  websocketConnection,
  onSuccess,
  token,
  agiliteUser
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [enabledVitals, setEnabledVitals] = useState({
    bloodPressure: true,
    pulse: true,
    temperature: true,
    oxygenSaturation: true
  })

  const vitalConfigs = [
    {
      key: 'bloodPressure',
      title: 'Blood Pressure',
      icon: faHeartPulse,
      color: '#FF4D4F',
      fields: [
        {
          name: ['bloodPressure', 'systolic'],
          label: 'Systolic',
          placeholder: '120',
          min: 60,
          max: 250,
          suffix: 'mmHg'
        },
        {
          name: ['bloodPressure', 'diastolic'],
          label: 'Diastolic',
          placeholder: '80',
          min: 40,
          max: 150,
          suffix: 'mmHg'
        }
      ]
    },
    {
      key: 'pulse',
      title: 'Pulse',
      icon: faHeartPulse,
      color: '#52C41A',
      fields: [
        {
          name: 'pulse',
          label: 'Heart Rate',
          placeholder: '72',
          min: 30,
          max: 250,
          suffix: 'bpm'
        }
      ]
    },
    {
      key: 'temperature',
      title: 'Temperature',
      icon: faTemperatureHalf,
      color: '#FA8C16',
      fields: [
        {
          name: 'temperature',
          label: 'Body Temperature',
          placeholder: '36.5',
          min: 30,
          max: 45,
          step: 0.1,
          suffix: '°C'
        }
      ]
    },
    {
      key: 'oxygenSaturation',
      title: 'Oxygen Saturation',
      icon: faDroplet,
      color: '#1890FF',
      fields: [
        {
          name: 'oxygenSaturation',
          label: 'SpO2',
          placeholder: '98',
          min: 50,
          max: 100,
          suffix: '%'
        }
      ]
    }
  ]

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const timestamp = dayjs()
      const dataPromises = []

      // Process blood pressure
      if (enabledVitals.bloodPressure && values.bloodPressure) {
        const { systolic, diastolic } = values.bloodPressure
        if (systolic && diastolic) {
          dataPromises.push(
            createClinicDataRecords({
              dateCreated: timestamp,
              createdBy: agiliteUser?._id,
              userRef: bookingData?.userRef || userRef,
              bookingRef: bookingData?._id || '',
              origin: 'booking',
              category: 'vitals',
              key: 'bloodPressure',
              value: [
                { key: 'systolic', value: systolic },
                { key: 'diastolic', value: diastolic }
              ]
            })
          )
        }
      }

      // Process other vitals
      const simpleVitals = ['pulse', 'temperature', 'oxygenSaturation']
      simpleVitals.forEach((vital) => {
        if (enabledVitals[vital] && values[vital]) {
          dataPromises.push(
            createClinicDataRecords({
              dateCreated: timestamp,
              createdBy: agiliteUser?._id,
              userRef: bookingData?.userRef || userRef,
              bookingRef: bookingData?._id || '',
              origin: 'booking',
              category: 'vitals',
              key: vital,
              value: values[vital]
            })
          )
        }
      })

      if (dataPromises.length === 0) {
        message.warning('Please enter at least one vital sign')
        setLoading(false)
        return
      }

      const results = await Promise.all(dataPromises)

      // Send via WebSocket for real-time updates
      if (bookingData?._id && websocketConnection?.isConnected) {
        websocketConnection.socket.send(
          JSON.stringify({
            bookingRef: bookingData._id,
            data: results
          })
        )
      }

      message.success('Vital signs captured successfully')

      // Show notification with details
      notification.success({
        message: 'Vital Signs Saved',
        description: (
          <div>
            <p style={{ margin: '8px 0' }}>Successfully captured:</p>
            <ul style={{ marginLeft: '20px', marginBottom: 0 }}>
              {enabledVitals.bloodPressure && values.bloodPressure && (
                <li>
                  Blood Pressure: {values.bloodPressure.systolic}/{values.bloodPressure.diastolic} mmHg
                </li>
              )}
              {enabledVitals.pulse && values.pulse && <li>Pulse: {values.pulse} bpm</li>}
              {enabledVitals.temperature && values.temperature && <li>Temperature: {values.temperature}°C</li>}
              {enabledVitals.oxygenSaturation && values.oxygenSaturation && (
                <li>Oxygen Saturation: {values.oxygenSaturation}%</li>
              )}
            </ul>
          </div>
        ),
        icon: <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#52c41a' }} />,
        placement: 'topRight',
        duration: 4
      })

      form.resetFields()
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error capturing vital signs:', error)

      // Show specific error message
      if (error.errorFields) {
        message.error('Please fill in all required fields')
      } else {
        message.error(handleError(error) || 'Failed to capture vital signs')
      }
    } finally {
      setLoading(false)
    }
  }, [form, enabledVitals, agiliteUser, bookingData, userRef, websocketConnection, onSuccess, onClose])

  const handleToggleVital = (vitalKey) => {
    setEnabledVitals((prev) => ({
      ...prev,
      [vitalKey]: !prev[vitalKey]
    }))
  }

  const getReferenceText = (key) => {
    const range = VITAL_SIGN_REFERENCE_RANGES[key]
    if (!range) return ''

    if (key === 'bloodPressure') {
      return `Normal: ${range.min[0]}/${range.min[1]} - ${range.max[0]}/${range.max[1]} ${range.unit}`
    }
    return `Normal: ${range.min} - ${range.max} ${range.unit}`
  }

  return (
    <Modal
      className='vital-signs-capture-modal'
      title={
        <Space>
          <FontAwesomeIcon icon={faStethoscope} style={{ color: token.colorPrimary }} />
          <Title level={4} style={{ margin: 0 }}>
            Capture Vital Signs
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key='cancel' onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key='submit'
          type='primary'
          loading={loading}
          onClick={handleSubmit}
          icon={<FontAwesomeIcon icon={faHeartPulse} />}
        >
          Save Vital Signs
        </Button>
      ]}
      centered
    >
      <Form form={form} layout='vertical'>
        <Row gutter={[16, 16]}>
          {vitalConfigs.map((config) => (
            <Col span={12} key={config.key}>
              <Card
                size='small'
                styles={{
                  body: {
                    background: enabledVitals[config.key] ? hexToRGBA(config.color, 0.05) : '#f5f5f5',
                    transition: 'all 0.3s ease'
                  },
                  header: {
                    background: enabledVitals[config.key] ? hexToRGBA(config.color, 0.1) : '#fafafa',
                    borderBottom: `2px solid ${enabledVitals[config.key] ? config.color : '#d9d9d9'}`,
                    padding: '8px 12px'
                  }
                }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Space size='small'>
                      <FontAwesomeIcon
                        icon={config.icon}
                        style={{
                          color: enabledVitals[config.key] ? config.color : '#999',
                          fontSize: '16px'
                        }}
                      />
                      <Text
                        strong
                        style={{
                          color: enabledVitals[config.key] ? '#000' : '#999'
                        }}
                      >
                        {config.title}
                      </Text>
                    </Space>
                    <Tooltip title={enabledVitals[config.key] ? 'Disable' : 'Enable'}>
                      <Switch
                        size='small'
                        checked={enabledVitals[config.key]}
                        onChange={() => handleToggleVital(config.key)}
                      />
                    </Tooltip>
                  </div>
                }
              >
                {enabledVitals[config.key] ? (
                  <>
                    <Row gutter={8}>
                      {config.fields.map((field) => (
                        <Col span={config.fields.length > 1 ? 12 : 24} key={field.name.toString()}>
                          <Form.Item
                            name={field.name}
                            label={field.label}
                            rules={[
                              {
                                required: true,
                                message: `Please enter ${field.label.toLowerCase()}`
                              }
                            ]}
                            style={{ marginBottom: 8 }}
                          >
                            <InputNumber
                              placeholder={field.placeholder}
                              min={field.min}
                              max={field.max}
                              step={field.step || 1}
                              addonAfter={field.suffix}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                      ))}
                    </Row>
                    <Text type='secondary' style={{ fontSize: '12px' }}>
                      {getReferenceText(config.key)}
                    </Text>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Text type='secondary'>Click the toggle to enable</Text>
                  </div>
                )}
              </Card>
            </Col>
          ))}
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <div style={{ textAlign: 'center' }}>
          <Text type='secondary'>
            Enable or disable individual vital signs as needed. All enabled fields must be filled.
          </Text>
        </div>
      </Form>
    </Modal>
  )
}

export default React.memo(VitalSignsCaptureModal)
